import React, { useState, useEffect } from 'react';
import { Row, Col, Typography, Card, Table, Tag, Button, Modal, Form, Input, Select, message, Space } from 'antd';
import MainLayout from '../../layouts/MainLayout';
import { financialApi } from '../../utils/financialApi';
import { extractApiUserMessage } from '../../utils/api';
import { formatPriceToman } from '../../utils/formatPriceToman';
import dayjs from 'dayjs';
import jalaliday from 'jalali-dayjs';

dayjs.extend(jalaliday);
const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const Approvals = () => {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);

  const [decideModalVisible, setDecideModalVisible] = useState(false);
  const [decideLoading, setDecideLoading] = useState(false);
  const [currentPayment, setCurrentPayment] = useState(null);
  const [form] = Form.useForm();

  const [matchModalVisible, setMatchModalVisible] = useState(false);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchForm] = Form.useForm();

  const fetchApprovals = async () => {
    try {
      setLoading(true);
      const res = await financialApi.getPendingApprovals();
      const data = res?.data?.pending_payments || res?.pending_payments || [];
      setApprovals(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch approvals:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApprovals();
  }, []);

  const handleDecideSubmit = async (values) => {
    try {
      setDecideLoading(true);
      const payload = { ...values };
      if (payload.bank_account_id) {
        payload.bank_account_id = Number(payload.bank_account_id);
      }
      await financialApi.decidePayment(currentPayment.id, payload);
      message.success('وضعیت پرداخت با موفقیت ثبت شد.');
      setDecideModalVisible(false);
      form.resetFields();
      fetchApprovals();
    } catch (error) {
      message.error(extractApiUserMessage(error) || 'خطا در ثبت وضعیت');
    } finally {
      setDecideLoading(false);
    }
  };

  const handleMatchSubmit = async (values) => {
    try {
      setMatchLoading(true);
      const payload = {
        ...values,
        order_id: Number(values.order_id),
        amount: Number(values.amount),
      };
      await financialApi.matchWebOrder(payload);
      message.success('سفارش وب با موفقیت تطبیق داده شد.');
      setMatchModalVisible(false);
      matchForm.resetFields();
    } catch (error) {
      message.error(extractApiUserMessage(error) || 'خطا در تطبیق سفارش');
    } finally {
      setMatchLoading(false);
    }
  };

  const openDecideModal = (record) => {
    setCurrentPayment(record);
    setDecideModalVisible(true);
  };

  const columns = [
    {
      title: 'شماره پیگیری',
      dataIndex: 'tracking_code',
      key: 'tracking_code',
      render: (text) => <Text className="font-semibold">{text || '-'}</Text>,
    },
    {
      title: 'معامله',
      dataIndex: ['deal', 'name'],
      key: 'deal_name',
    },
    {
      title: 'مبلغ (تومان)',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => formatPriceToman(amount || 0),
    },
    {
      title: 'وضعیت',
      dataIndex: 'approval_status',
      key: 'approval_status',
      render: (status) => (
        <Tag color="warning">در انتظار تایید</Tag>
      ),
    },
    {
      title: 'فایل فیش',
      dataIndex: 'receipt_file',
      key: 'receipt_file',
      render: (file) => file ? <a href={file} target="_blank" rel="noreferrer">مشاهده</a> : '-',
    },
    {
      title: 'عملیات',
      key: 'action',
      render: (_, record) => (
        <Button type="primary" size="small" onClick={() => openDecideModal(record)}>
          بررسی و تصمیم‌گیری
        </Button>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="p-4 md:p-6 pb-24 max-w-[1600px] mx-auto animate-fade-in">
        <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <Title level={3} className="m-0 text-gray-800 dark:text-gray-100 font-bold">
              کارتابل تایید واریزی‌ها
            </Title>
            <Text className="text-gray-500 dark:text-gray-400">
              بررسی، تایید یا رد واریزی‌های معامله و تطبیق سفارشات وب
            </Text>
          </div>
          <Button type="primary" onClick={() => setMatchModalVisible(true)}>
            تطبیق سفارش وب
          </Button>
        </div>

        <Card 
          loading={loading}
          bordered={false} 
          className="shadow-sm dark:bg-gray-800 rounded-2xl h-full"
        >
          <Table 
            dataSource={approvals} 
            columns={columns}
            rowKey="id"
            pagination={false}
            size="middle"
            className="custom-table"
            locale={{ emptyText: 'واریزی در انتظار تایید یافت نشد' }}
          />
        </Card>

        {/* Decide Modal */}
        <Modal
          title="تصمیم‌گیری پرداخت"
          open={decideModalVisible}
          onCancel={() => setDecideModalVisible(false)}
          footer={null}
        >
          <Form form={form} layout="vertical" onFinish={handleDecideSubmit}>
            <Form.Item name="decision" label="تصمیم" rules={[{ required: true, message: 'انتخاب تصمیم الزامی است' }]}>
              <Select placeholder="انتخاب کنید">
                <Option value="approved">تایید</Option>
                <Option value="rejected">رد</Option>
              </Select>
            </Form.Item>
            
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.decision !== currentValues.decision}
            >
              {({ getFieldValue }) => {
                const decision = getFieldValue('decision');
                if (decision === 'approved') {
                  return (
                    <Form.Item name="bank_account_id" label="شناسه حساب بانکی" rules={[{ required: true }]}>
                      <Input type="number" placeholder="مثال: 1" />
                    </Form.Item>
                  );
                }
                if (decision === 'rejected') {
                  return (
                    <Form.Item name="rejection_reason" label="دلیل رد" rules={[{ required: true }]}>
                      <TextArea rows={3} placeholder="دلیل رد را وارد کنید..." />
                    </Form.Item>
                  );
                }
                return null;
              }}
            </Form.Item>

            <Form.Item className="mb-0 flex justify-end">
              <Space>
                <Button onClick={() => setDecideModalVisible(false)}>انصراف</Button>
                <Button type="primary" htmlType="submit" loading={decideLoading}>ثبت</Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Match Web Order Modal */}
        <Modal
          title="تطبیق خودکار سفارش وب"
          open={matchModalVisible}
          onCancel={() => setMatchModalVisible(false)}
          footer={null}
        >
          <Form form={matchForm} layout="vertical" onFinish={handleMatchSubmit}>
            <Form.Item name="order_id" label="شناسه سفارش (Order ID)" rules={[{ required: true }]}>
              <Input type="number" />
            </Form.Item>
            <Form.Item name="customer_phone" label="موبایل مشتری" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="amount" label="مبلغ پرداخت شده" rules={[{ required: true }]}>
              <Input type="number" />
            </Form.Item>
            <Form.Item name="gateway" label="درگاه" rules={[{ required: true }]}>
              <Input placeholder="مانند: Zarinpal" />
            </Form.Item>
            <Form.Item name="transaction_id" label="شناسه تراکنش" rules={[{ required: true }]}>
              <Input />
            </Form.Item>

            <Form.Item className="mb-0 flex justify-end">
              <Space>
                <Button onClick={() => setMatchModalVisible(false)}>انصراف</Button>
                <Button type="primary" htmlType="submit" loading={matchLoading}>تطبیق</Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default Approvals;
